import { getApiBaseUrl } from '@/lib/api';

/**
 * HTML Template Agent API Service
 * Provides AI-powered HTML template generation assistance for HTML Template nodes
 * Uses SSE streaming for real-time response delivery
 */

export interface HtmlTemplateAgentMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface HtmlTemplateAgentInputNode {
  nodeId: string;
  nodeName: string;
  sampleData: unknown;
}

export interface HtmlTemplateAgentImage {
  /** Base64 encoded image data (without data URL prefix) */
  data: string;
  /** MIME type of the image (e.g., "image/png", "image/jpeg") */
  mediaType: string;
}

export interface HtmlTemplateAgentChatRequest {
  messages: HtmlTemplateAgentMessage[];
  inputSchema: HtmlTemplateAgentInputNode[];
  existingTemplate?: string;
  primaryColor?: string;
  currencyCode?: string;
  dateFormat?: string;
  /** Output type - 'html' for web/PDF or 'email' for email-safe HTML */
  outputType?: 'html' | 'email';
  /** Optional reference image (mockup, screenshot, design inspiration) */
  referenceImage?: HtmlTemplateAgentImage;
}

export interface HtmlTemplateAgentStreamEvent {
  type: 'chunk' | 'done' | 'error';
  content?: string;
  fullResponse?: string;
  tokensUsed?: number;
  error?: string;
}

export interface HtmlTemplateAgentChatOptions {
  onChunk?: (content: string) => void;
  onComplete?: (fullResponse: string, tokensUsed: number) => void;
  onError?: (error: string) => void;
  getAccessToken: () => Promise<string>;
  subscriptionId?: string;
}

/**
 * Chat with the HTML Template Agent using SSE streaming
 * @param request - The chat request with messages and context
 * @param options - Callbacks for handling streaming events
 * @returns AbortController to cancel the request if needed
 */
export function chatWithHtmlTemplateAgent(
  request: HtmlTemplateAgentChatRequest,
  options: HtmlTemplateAgentChatOptions
): AbortController {
  const abortController = new AbortController();

  (async () => {
    try {
      const token = await options.getAccessToken();
      const baseUrl = getApiBaseUrl();

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      };

      if (options.subscriptionId) {
        headers['X-Subscription-Id'] = options.subscriptionId;
      }

      const response = await fetch(`${baseUrl}/api/htmltemplateagent/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to connect to HTML Template Agent';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch {
          if (errorText) {
            errorMessage = errorText;
          }
        }
        options.onError?.(errorMessage);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        options.onError?.('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process SSE events (data: {...}\n\n)
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6).trim();
            if (!jsonStr) continue;

            try {
              const event: HtmlTemplateAgentStreamEvent = JSON.parse(jsonStr);

              switch (event.type) {
                case 'chunk':
                  if (event.content) {
                    options.onChunk?.(event.content);
                  }
                  break;

                case 'done':
                  options.onComplete?.(
                    event.fullResponse || '',
                    event.tokensUsed || 0
                  );
                  break;

                case 'error':
                  options.onError?.(event.error || 'Unknown error');
                  break;
              }
            } catch (parseError) {
              console.warn('[HtmlTemplateAgent] Failed to parse SSE event:', jsonStr);
            }
          }
        }
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        // Request was cancelled, don't report as error
        return;
      }
      console.error('[HtmlTemplateAgent] Error:', error);
      options.onError?.((error as Error).message || 'Connection error');
    }
  })();

  return abortController;
}

/**
 * Extract HTML code blocks from AI response
 * Returns array of { language, code } objects
 */
export function extractHtmlCodeBlocks(
  response: string
): Array<{ language: string; code: string }> {
  const codeBlocks: Array<{ language: string; code: string }> = [];

  // Match ```language\ncode\n``` pattern
  const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
  let match;

  while ((match = codeBlockRegex.exec(response)) !== null) {
    const language = match[1] || 'html';
    const code = match[2].trim();
    codeBlocks.push({ language, code });
  }

  return codeBlocks;
}

/**
 * Check if a response contains any code blocks
 */
export function hasHtmlCodeBlocks(response: string): boolean {
  return /```\w*\n[\s\S]*?```/.test(response);
}
